import { useState } from 'react';
import { Badge, Card, Group, Stack, Table, Text, Title } from '@mantine/core';
import {
  useIsyeriDurumGecisi,
  useIsyeriZorunluGorevlendirmeler,
  useCompanies,
  useWorkplacesTumu,
  useKapilar,
} from '../api/queries';
import { ISYERI_DURUM_ETIKET, TEHLIKE_SINIFI_ETIKET } from '../i18n/tr';
import { SatirEylemleri, KapiSebebi } from '../sozlesme';
import {
  IsyeriEkleSatiri,
  SirketAdiDuzenle,
  SirketEkleSatiri,
  YazmaHatasi,
  useSirketSilme,
} from './organizasyon-parts';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';

/**
 * Şirketler — TEK SEKME (S10 gezinti kararı, 05.08.2026): şirkete göre GRUPLU
 * işyeri listesi; ayrı "İşyerleri" sekmesi kalktı. Katman ayrımı tek satırla
 * anlatılır: işyeri = SGK işyeri sicil dosyası. SGK sicil kolonu İŞYERİ
 * satırındadır — şirket satırında sicil YOK (sicil işyerinindir, ERD §2).
 *
 * ✏️ F4·S3 (madde 83): NACE ve TEHLİKE SINIFI da İŞYERİ satırına indi —
 * **şirketin tehlike sınıfı diye bir şey yoktur**, SGK sınıfı SİCİL bazında
 * belirler; aynı şirketin genel müdürlüğü az tehlikeli, santrali çok
 * tehlikeli olabilir. Sınıf NACE'den TÜRETİLMİŞ değerdir — ekran yalnız
 * gösterir (Kapsam §3.2). ÇALIŞAN SAYISI da türetilmiş bir sayıdır (kolon
 * değil) ve yüklenici personelini SAYMAZ.
 *
 * Arşivli işyeri soluk + rozetli; okuma serbest (§2.15).
 */
export function CompaniesScreen(): React.JSX.Element {
  const companies = useCompanies();
  // ✏️ **TUR 9 / B4 (T9-18·T9-19): TEK İSTEK.** Eskiden şirket başına ayrı
  // sorgu atılıyordu (`useWorkplacesOf` fan-out'u) ve **bir isteğin sessizce
  // boş dönmesi hiçbir yerde görünmüyordu** (madde 180'in sınıfı).
  const isyerleri = useWorkplacesTumu();
  // P13-b (madde 225): üç kapı SUNUCUDAN (`GET kapilar`) — şirket §20.5/A4 · işyeri
  // holding + şirket yön. · arşiv §20.5/A3; ekran şapka dizgesi yazmaz.
  const kapilar = useKapilar();
  const sirketYazar = kapilar.data?.sirketKapisi.yazabilir ?? false;
  const isyeriYazar = kapilar.data?.isyeriKapisi.yazabilir ?? false;
  const arsivYazar = kapilar.data?.arsivKapisi.yazabilir ?? false;
  // Sessiz 403 paketi (madde 283): zorunlu görevlendirme kolonu yalnız okuyabilen
  // şapkada çizilir; kapalıysa uç da çağrılmaz (eskiden hücre "—" kalıyordu).
  const zorunluOkur = kapilar.data?.ikincilOkuma.zorunluGorevlendirme ?? false;
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);
  const silme = useSirketSilme();

  return (
    <Stack data-testid="sirket-listesi">
      <Title order={3}>Şirketler</Title>
      <Text size="xs" c="dimmed">
        İşyeri = SGK işyeri sicil dosyası.
      </Text>
      <QueryStatus queries={[companies, isyerleri]} />
      {sirketYazar && <SirketEkleSatiri />}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.sirketKapisi}
          nesne="Şirketi"
          fiil="açar"
          testId="sirket-yazamama-sebebi"
        />
      )}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.arsivKapisi}
          nesne="İşyerini"
          fiil="arşivler"
          testId="isyeri-arsiv-yazamama-sebebi"
        />
      )}
      <YazmaHatasi hata={silme.hata} testId="sirket-sil-hata" />
      {companies.data?.length === 0 && (
        <BosListeNotu mesaj="Bahçenizde görüntülenecek şirket yok — kapsam ataması için holding yöneticinize başvurun." />
      )}
      {companies.data?.map((company) => {
        const workplaces = isyerleri.data?.filter((w) => w.companyId === company.id);
        return (
          <Card withBorder padding="md" key={company.id} data-testid="sirket-satiri">
            <Group justify="space-between" mb="xs">
              <Text fw={600}>{company.ad}</Text>
              {sirketYazar && (
                <SatirEylemleri
                  testId={`sirket-eylem-${company.id}`}
                  digerleri={[
                    {
                      etiket: 'Adı düzelt',
                      onClick: () => setDuzenlenen(company.id),
                      testId: 'sirket-duzenle-ac',
                    },
                    {
                      etiket: 'Şirketi sil',
                      onClick: () => silme.sil(company.id),
                      tehlikeli: true,
                      testId: 'sirket-sil',
                    },
                  ]}
                />
              )}
            </Group>
            {duzenlenen === company.id && (
              <SirketAdiDuzenle
                companyId={company.id}
                mevcutAd={company.ad}
                onKapat={() => setDuzenlenen(null)}
              />
            )}
            {workplaces?.length === 0 && <BosListeNotu mesaj="Kayıtlı işyeri yok." />}
            {isyeriYazar && <IsyeriEkleSatiri companyId={company.id} />}
            {kapilar.data && (
              <KapiSebebi
                durum={kapilar.data.isyeriKapisi}
                nesne="İşyerini"
                fiil="açar"
                testId={`isyeri-yazamama-sebebi-${company.id}`}
              />
            )}
            {workplaces && workplaces.length > 0 && (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>İşyeri</Table.Th>
                    <Table.Th>SGK sicil</Table.Th>
                    <Table.Th>NACE</Table.Th>
                    <Table.Th>Tehlike sınıfı</Table.Th>
                    {/* Kurul (50+) · ekipler (≥10) · küçük işyeri (&lt;10)
                        eşiklerinin okunduğu sayı — TÜRETİLİR, kolon değil. */}
                    <Table.Th>Çalışan</Table.Th>
                    <Table.Th>Durum</Table.Th>
                    {/* ✏️ TUR 8 / A1 — "İlk yardımcı" KOLONU KALKTI. Aynı
                        soruyu (kaç ilkyardımcı var) yandaki kolonla İKİ FARKLI
                        KAYNAKTAN cevaplıyordu: bu belgeden, o görevlendirmeden.
                        Sayım tek motorda birleşti; ilkyardımcı satırı zorunlu
                        görevlendirme hücresinde görünür. */}
                    {/* 5½ / B3 — ZORUNLU GÖREVLENDİRMELER (§25/12): türetilir,
                        gösterir, engellemez. */}
                    {zorunluOkur && <Table.Th>Zorunlu görevlendirme</Table.Th>}
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {workplaces.map((w) => (
                    <Table.Tr
                      key={w.id}
                      data-testid="isyeri-satiri"
                      opacity={w.durum === 'archived' ? 0.55 : 1}
                    >
                      <Table.Td>{w.ad}</Table.Td>
                      <Table.Td>{w.sgkSicil}</Table.Td>
                      <Table.Td>{w.naceKod}</Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={
                            w.tehlikeSinifi === 'highly_hazardous'
                              ? ANLAM.engel
                              : w.tehlikeSinifi === 'hazardous'
                                ? ANLAM.dikkat
                                : ANLAM.olumlu
                          }
                          data-testid="isyeri-tehlike-sinifi"
                        >
                          {TEHLIKE_SINIFI_ETIKET[w.tehlikeSinifi]}
                        </Badge>
                      </Table.Td>
                      <Table.Td data-testid="isyeri-calisan-sayisi">{w.calisanSayisi}</Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={
                            w.durum === 'active'
                              ? ANLAM.olumlu
                              : w.durum === 'archived'
                                ? ANLAM.bilgi
                                : ANLAM.dikkat
                          }
                        >
                          {ISYERI_DURUM_ETIKET[w.durum]}
                        </Badge>
                      </Table.Td>
                      {zorunluOkur && (
                        <Table.Td>
                          <ZorunluGorevlendirmeHucresi workplaceId={w.id} />
                        </Table.Td>
                      )}
                      <Table.Td>
                        <IsyeriEylemleri
                          workplaceId={w.id}
                          durum={w.durum}
                          arsivYazar={arsivYazar}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Card>
        );
      })}
    </Stack>
  );
}

/**
 * İŞYERİ SATIR EYLEMLERİ (TUR 9 / B1): birincil = **kartı aç** (künye +
 * lokasyon ağacı ayrı sayfada — P1); arşivleme ⋮ altında ve `tehlikeli`
 * (geri alınabilir ama faturalamayı durduran bir geçiştir — P2-a).
 *
 * ⚠️ **GEÇİŞİ YALNIZ HOLDİNG YÖNETİCİSİ TETİKLER** (§20.5/A3): diğer
 * şapkalarda eylem **çizilmez** — çalışamayacak kontrol gösterilmez (§2.40).
 */
function IsyeriEylemleri({
  workplaceId,
  durum,
  arsivYazar,
}: {
  workplaceId: string;
  durum: 'active' | 'archived' | 'suspended';
  arsivYazar: boolean;
}): React.JSX.Element {
  const gecis = useIsyeriDurumGecisi();
  return (
    <Group gap="xs" wrap="nowrap">
      <SatirEylemleri
        testId={`isyeri-eylem-${workplaceId}`}
        birincil={{
          etiket: 'Aç',
          to: `/organizasyon/isyeri/${workplaceId}`,
          testId: 'isyeri-ac',
        }}
        digerleri={[
          {
            etiket: durum === 'archived' ? 'Arşivden geri aç' : 'Arşivle',
            gizli: !arsivYazar || durum === 'suspended',
            tehlikeli: durum !== 'archived',
            testId: 'isyeri-arsiv',
            onClick: () =>
              gecis.mutate({
                workplaceId,
                hedef: durum === 'archived' ? 'activate' : 'archive',
              }),
          },
        ]}
      />
      <YazmaHatasi hata={gecis.error} testId={`isyeri-gecis-hata-${workplaceId}`} />
    </Group>
  );
}

/**
 * 5½ / B3 — ZORUNLU GÖREVLENDİRME hücresi (Kapsam §25/12). *"Bu işyeri hangi
 * görevlendirmeden kaç kişi taşımalı?"* — TÜRETİLİR; sistem gösterir,
 * engellemez. Kuralı sayı üretmeyen türler listeye girmez; hiç satır yoksa
 * hücre SESSİZ kalır (`isyeri_oran` deseni: sistem bilmediği şeye "eksik" demez).
 */
function ZorunluGorevlendirmeHucresi({ workplaceId }: { workplaceId: string }): React.JSX.Element {
  const zorunlu = useIsyeriZorunluGorevlendirmeler(workplaceId);
  const veri = zorunlu.data;
  if (!veri || veri.satirlar.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Group gap="xs" data-testid={`zorunlu-gorevlendirme-${workplaceId}`}>
      <Text size="sm">
        {veri.satirlar.reduce((t, s) => t + s.mevcut, 0)} /{' '}
        {veri.satirlar.reduce((t, s) => t + s.gereken, 0)}
      </Text>
      {veri.toplamEksik > 0 && (
        <Badge color={ANLAM.dikkat} size="sm" variant="light">
          {veri.toplamEksik} eksik
        </Badge>
      )}
    </Group>
  );
}
